import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { EnvelopeRotate } from 'src/components/icons';
import { LIST_INVITATIONS_RESOURCE } from 'src/features/users/api/useListInvitations';
import { useResendInvitation } from 'src/features/users/api/useResendInvitation';
import { userMessages } from 'src/features/users/utils/messages';
import { Invitation } from 'src/features/users/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { generalErrorMessages } from 'src/utils/messages';

interface ResendInvitationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedInvitation: Invitation;
    children?: React.ReactNode;
}

function ResendInvitationFormModal<TData>({ selectedInvitation, children }: ResendInvitationFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: resendInvitation, isPending } = useResendInvitation({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_INVITATIONS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(userMessages.resendInvitationSuccess),
                description: formatMessage(userMessages.resendInvitationSuccessDetails, {
                    email: selectedInvitation.email,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => resendInvitation({ parkingId, invitationId: selectedInvitation.id });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon>
                            <EnvelopeRotate className="size-5" />
                        </SquareIcon>
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...userMessages.resendInvitation} />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={userMessages.resendInvitationTo.id}
                                    defaultMessage={userMessages.resendInvitationTo.defaultMessage}
                                    values={{
                                        email: selectedInvitation.email,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={userMessages.confirmResendInvitation.id}
                            defaultMessage={userMessages.confirmResendInvitation.defaultMessage}
                            values={{
                                email: selectedInvitation.email,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button disabled={isPending} type="submit" onClick={onSubmit} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        <FormattedMessage {...userMessages.resendInvitation} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ResendInvitationFormModal;
